"use client";

import React, { useMemo, useState } from "react";
import { Droplets, Fuel, FlaskConical, CircleCheck as CheckCircle2, ArrowRight, SlidersHorizontal as Sliders, Gauge, FileText, Layers, ShieldCheck, Waves, Flame } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import DataTable, { type DataTableColumn } from "@/components/common/DataTable";
import Reveal from "@/components/common/Reveal";
import { useLanguage } from "@/hooks/useLanguage";
import { pick, type Lang } from "@/lib/i18n";
interface ApplicationPillar {
  id: string;
  title: Record<Lang, string>;
  description: Record<Lang, string>;
  icon: React.ComponentType<{
    className?: string;
  }>;
  metric: Record<Lang, string>;
  grade: string;
  to: string;
}
const applicationPillars: ApplicationPillar[] = [{
  id: "water",
  title: {
    en: "Process & intake water",
    zh: "工艺水与进水过滤",
    es: "Agua de proceso y captación",
    de: "Prozess- und Zulaufwasser",
    fr: "Eau de procédé et prise d'eau",
    pt: "Água de processo e captação"
  },
  description: {
    en: "Sand removal, cooling loops, and ultra-fine particulate separation from 5 to 500 microns.",
    zh: "除砂、冷却循环回路以及 5 至 500 微米的超细颗粒物截留分离。",
    es: "Eliminación de arena, circuitos de refrigeración y retención de partículas finas de 5 a 500 micras.",
    de: "Sandabscheidung, Kühlkreisläufe und Feinstpartikel-Rückhaltung von 5 bis 500 Mikrometer.",
    fr: "Déssablage, boucles de refroidissement et séparation de particules fines de 5 à 500 microns.",
    pt: "Remoção de areia, circuitos de resfriamento e retenção de partículas finas de 5 a 500 micras."
  },
  icon: Waves,
  metric: {
    en: "Down to 5 µm",
    zh: "低至 5 µm",
    es: "Hasta 5 µm",
    de: "Bis zu 5 µm",
    fr: "Jusqu'à 5 µm",
    pt: "Até 5 µm"
  },
  grade: "AISI 304 / 316L",
  to: "/WaterLiquidFiltrationPage"
}, {
  id: "oil",
  title: {
    en: "Lube & hydraulic oil",
    zh: "润滑油与液压油",
    es: "Aceite lubricante e hidráulico",
    de: "Schmier- und Hydrauliköl",
    fr: "Huile de lubrification et hydraulique",
    pt: "Óleo lubrificante e hidráulico"
  },
  description: {
    en: "High delta-P structural mesh preventing system wear in heavy equipment and turbines.",
    zh: "高耐压差结构网，有效防止重型机械与汽轮机系统的部件磨损。",
    es: "Malla estructural de alta presión diferencial para prevenir desgaste en maquinaria pesada y turbinas.",
    de: "Strukturgewebe für hohe Differenzdrücke zur Vermeidung von Verschleiß an Großmaschinen und Turbinen.",
    fr: "Toile structurelle haute pression différentielle prévenant l'usure des machines lourdes et turbines.",
    pt: "Malha estrutural de alta pressão diferencial para prevenir desgaste em equipamentos pesados e turbinas."
  },
  icon: Droplets,
  metric: {
    en: "High pressure",
    zh: "高耐压差",
    es: "Alta presión",
    de: "Hochdruckfest",
    fr: "Haute pression",
    pt: "Alta pressão"
  },
  grade: "AISI 316L / 304",
  to: "/OilFuelFiltrationPage"
}, {
  id: "fuel",
  title: {
    en: "Aviation & marine fuel",
    zh: "航空与船用燃油",
    es: "Combustible de aviación y marino",
    de: "Flug- und Schiffskraftstoffe",
    fr: "Carburants aviation et marine",
    pt: "Combustíveis de aviação e marítimos"
  },
  description: {
    en: "Cleanable, spark-safe twilled weaves for kerosene, diesel, and high-flow bunkering.",
    zh: "适用于航空煤油、柴油及大流量加油的可清洗、抗静电密纹编织网。",
    es: "Tejidos sarga lavables y seguros para queroseno, diésel y repostaje de alto caudal.",
    de: "Reinigbare, funkensichere Köpertressen für Kerosin, Diesel und Hochleistungs-Bunkerung.",
    fr: "Tissages sergés lavables et sécurisés pour kérosène, diesel et soutage à haut débit.",
    pt: "Tramas sarjas laváveis e seguras para querosene, diesel e abastecimento de alta vazão."
  },
  icon: Flame,
  metric: {
    en: "ISO 4406 Compliant",
    zh: "符合 ISO 4406",
    es: "Conforme a ISO 4406",
    de: "Konform mit ISO 4406",
    fr: "Conforme ISO 4406",
    pt: "Conforme ISO 4406"
  },
  grade: "Monel / SS 316L",
  to: "/OilFuelFiltrationPage"
}, {
  id: "chemical",
  title: {
    en: "Aggressive chemicals",
    zh: "腐蚀性化学介质",
    es: "Productos químicos agresivos",
    de: "Aggressive Chemikalien",
    fr: "Produits chimiques agressifs",
    pt: "Produtos químicos agressivos"
  },
  description: {
    en: "Corrosion-resistant alloys for acidic baths, solvent recovery, and catalyst retention.",
    zh: "特种耐腐蚀合金网，专用于酸洗槽、溶剂回收及催化剂拦截。",
    es: "Aleaciones resistentes a la corrosión para baños ácidos, recuperación de solventes y retención de catalizadores.",
    de: "Korrosionsbeständige Legierungen für Säurebäder, Lösemittelrückgewinnung und Katalysatorrückhaltung.",
    fr: "Alliages anticorrosion pour bains acides, récupération de solvants et rétention de catalyseurs.",
    pt: "Ligas anticorrosivas para banhos ácidos, recuperação de solventes e retenção de catalisadores."
  },
  icon: FlaskConical,
  metric: {
    en: "pH 1 to 14 Safe",
    zh: "耐受 pH 1 至 14",
    es: "Apto pH 1 a 14",
    de: "pH 1 bis 14 beständig",
    fr: "Résistant pH 1 à 14",
    pt: "Resistente a pH 1 a 14"
  },
  grade: "Nickel Alloy / Hastelloy",
  to: "/ChemicalFiltrationPage"
}];
interface ProcessStep {
  step: string;
  title: Record<Lang, string>;
  desc: Record<Lang, string>;
  focus: Record<Lang, string>;
}
const processSteps: ProcessStep[] = [{
  step: "01",
  title: {
    en: "Fluid & viscosity profile",
    zh: "流体与粘度特性分析",
    es: "Perfil de fluido y viscosidad",
    de: "Fluid- und Viskositätsprofil",
    fr: "Profil de fluide et viscosité",
    pt: "Perfil de fluido e viscosidade"
  },
  desc: {
    en: "Define medium type, dynamic viscosity, flow velocity, and operating temperatures to predict shear and flow resistance.",
    zh: "明确介质类型、动力粘度、流速和操作温度，以准确评估流体剪切力与流动阻力。",
    es: "Defina tipo de medio, viscosidad dinámica, velocidad de flujo y temperaturas para predecir cizallamiento y resistencia hidráulica.",
    de: "Bestimmen Sie Medientyp, dynamische Viskosität, Fließgeschwindigkeit und Betriebstemperaturen zur Berechnung von Scher- und Strömungswiderstand.",
    fr: "Définissez le type de fluide, la viscosité dynamique, la vitesse et la température de service pour anticiper cisaillement et pertes de charge.",
    pt: "Defina o tipo de fluido, viscosidade dinâmica, velocidade de fluxo e temperaturas operacionais para estimar cisalhamento e resistência hidráulica."
  },
  focus: {
    en: "Viscosity, temperature & flow velocity",
    zh: "粘度、操作温度与流动流速",
    es: "Viscosidad, temperatura y velocidad de flujo",
    de: "Viskosität, Temperatur & Fließgeschwindigkeit",
    fr: "Viscosité, température et vitesse d'écoulement",
    pt: "Viscosidade, temperatura e velocidade de fluxo"
  }
}, {
  step: "02",
  title: {
    en: "Particle cut-off & micron rating",
    zh: "颗粒截留与微米精度",
    es: "Corte de partículas y micraje",
    de: "Partikelabscheidung & Mikronwert",
    fr: "Seuil de coupure et micronage",
    pt: "Corte de partículas e micragem"
  },
  desc: {
    en: "Establish nominal vs. absolute retention requirements, contaminant load, and acceptable downstream particle limits.",
    zh: "确定公称与绝对截留精度、杂质负荷量以及下游允许的颗粒残留限值。",
    es: "Establezca requisitos nominales frente a absolutos, carga de contaminantes y límites admisibles de partículas aguas abajo.",
    de: "Legen Sie nominale bzw. absolute Abscheidegrade, Schmutzbeladung und zulässige Partikelgrenzen nach dem Filter fest.",
    fr: "Déterminez la rétention nominale ou absolue, la charge en impuretés et les seuils admissibles de particules en aval.",
    pt: "Defina retenção nominal versus absoluta, carga de impurezas e limites aceitáveis de partículas a jusante."
  },
  focus: {
    en: "Target pore size & retention accuracy",
    zh: "目标微米孔径与截留精度",
    es: "Tamaño de poro objetivo y precisión de corte",
    de: "Zielporenweite & Abscheidegenauigkeit",
    fr: "Taille de pore cible et précision de rétention",
    pt: "Tamanho de poro e precisão de retenção"
  }
}, {
  step: "03",
  title: {
    en: "Differential pressure & geometry",
    zh: "压降计算与滤网结构",
    es: "Presión diferencial y geometría",
    de: "Differenzdruck & Geometrie",
    fr: "Pression différentielle et géométrie",
    pt: "Pressão diferencial e geometria"
  },
  desc: {
    en: "Calculate allowable clean and dirty ΔP, structural pack support, backwash force, and pleating or cartridge configuration.",
    zh: "计算初始洁净与极限压降 ΔP、结构支撑强度、反冲洗承受力及折波/滤筒结构形式。",
    es: "Calcule la caída de presión limpia y sucia admisible, soporte estructural, fuerza de retrolavado y forma de plisado o cartucho.",
    de: "Berechnen Sie zulässigen Rein- und Schmutzdruckverlust ΔP, Stützgeometrie, Rückspülkräfte sowie Plissier- oder Zylinderbauform.",
    fr: "Calculez les pertes de charge propres et colmatées admissibles, le renfort structurel, l'effort de rétrolavage et la forme plissée ou cylindrique.",
    pt: "Calcule a perda de carga limpa e colmatada admissível, suporte estrutural, esforço de retrolavagem e formato pregueado ou cartucho."
  },
  focus: {
    en: "Mechanical burst pressure & ΔP threshold",
    zh: "机械耐破压与 ΔP 极限阈值",
    es: "Presión de rotura mecánica y límite de ΔP",
    de: "Mechanischer Berstdruck & ΔP-Grenzwerte",
    fr: "Pression d'éclatement mécanique et seuil de ΔP",
    pt: "Pressão de ruptura mecânica e limite de ΔP"
  }
}, {
  step: "04",
  title: {
    en: "Alloy & corrosion compatibility",
    zh: "合金耐腐蚀与化学兼容性",
    es: "Aleación y compatibilidad anticorrosiva",
    de: "Legierung & Korrosionsbeständigkeit",
    fr: "Alliage et compatibilité chimique",
    pt: "Liga e compatibilidade química"
  },
  desc: {
    en: "Select between AISI 304, 316L, or specialty nickel alloys based on pH levels, chloride exposure, and cleaning chemistry.",
    zh: "根据 pH 值、氯离子浓度及化学清洗工艺，选择 AISI 304、316L 或特种镍基合金。",
    es: "Seleccione entre AISI 304, 316L o aleaciones especiales de níquel según niveles de pH, cloruros y agentes de limpieza química.",
    de: "Wählen Sie zwischen AISI 304, 316L oder Nickel-Sonderlegierungen je nach pH-Wert, Chloridbelastung und chemischer Reinigung.",
    fr: "Choisissez entre AISI 304, 316L ou alliages de nickel spéciaux selon le pH, l'exposition aux chlorures et les produits de nettoyage.",
    pt: "Selecione entre AISI 304, 316L ou ligas especiais de níquel com base no pH, exposição a cloretos e química de limpeza."
  },
  focus: {
    en: "PREN index & chemical corrosion immunity",
    zh: "PREN 耐点蚀指数与耐化学腐蚀性",
    es: "Índice PREN y resistencia química",
    de: "PREN-Index & chemische Korrosionsbeständigkeit",
    fr: "Indice PREN et résistance chimique",
    pt: "Índice PREN e resistência química"
  }
}];
interface SpecRow {
  id: string;
  application: Record<Lang, string>;
  meshType: Record<Lang, string>;
  micron: string;
  alloy: string;
}
const waterSpecs: SpecRow[] = [{
  id: "water-1",
  application: {
    en: "Intake Straining & Pre-filtration",
    zh: "进水粗滤与预过滤",
    es: "Desbaste de toma y prefiltración",
    de: "Zulauf-Grobsiebung & Vorfiltration",
    fr: "Prétraitement d'eau brute et préfiltration",
    pt: "Pré-filtragem e retenção de captação"
  },
  meshType: {
    en: "Plain Square Weave",
    zh: "平纹方孔网",
    es: "Malla cuadrada lisa",
    de: "Glatte Quadratmasche",
    fr: "Toile carrée unie",
    pt: "Malha quadrada lisa"
  },
  micron: "250 – 1000 µm",
  alloy: "304 / 316L"
}, {
  id: "water-2",
  application: {
    en: "Cooling Tower & Process Loop",
    zh: "冷却塔与工艺循环回路",
    es: "Torre de refrigeración y circuito de proceso",
    de: "Kühlturm & Prozesskreislauf",
    fr: "Tour de refroidissement et boucle de procédé",
    pt: "Torre de resfriamento e circuito de processo"
  },
  meshType: {
    en: "Twill Square Weave",
    zh: "斜纹方孔网",
    es: "Malla cuadrada sarga",
    de: "Köper-Quadratmasche",
    fr: "Toile carrée sergée",
    pt: "Malha quadrada sarja"
  },
  micron: "50 – 150 µm",
  alloy: "316L"
}, {
  id: "water-3",
  application: {
    en: "Wastewater & Backwash Screens",
    zh: "污水处理与反冲洗滤网",
    es: "Aguas residuales y mallas de retrolavado",
    de: "Abwasser & Rückspülsiebe",
    fr: "Eaux usées et grilles de rétrolavage",
    pt: "Efluentes e telas de retrolavagem"
  },
  meshType: {
    en: "Sintered Multi-Layer",
    zh: "多层烧结金属网",
    es: "Multicapa sinterizada",
    de: "Mehrlagiges Sintergewebe",
    fr: "Multicouche fritté",
    pt: "Multicamadas sinterizada"
  },
  micron: "20 – 100 µm",
  alloy: "316L / 304"
}, {
  id: "water-4",
  application: {
    en: "Polishing & Reverse Osmosis Guard",
    zh: "精滤与反渗透保安过滤",
    es: "Afinado y protección de ósmosis inversa",
    de: "Polierfiltration & Umkehrosmose-Schutz",
    fr: "Finishing et protection d'osmose inverse",
    pt: "Polimento e proteção para osmose reversa"
  },
  meshType: {
    en: "Plain Dutch Weave (SPW)",
    zh: "平纹密纹网 (SPW)",
    es: "Tejido Dutch liso (SPW)",
    de: "Glatte Tresse (SPW)",
    fr: "Tissage hollandais uni (SPW)",
    pt: "Trama holandesa lisa (SPW)"
  },
  micron: "5 – 25 µm",
  alloy: "316L"
}];
const oilSpecs: SpecRow[] = [{
  id: "oil-1",
  application: {
    en: "High-Pressure Hydraulic Circuits",
    zh: "高压液压系统管路",
    es: "Circuitos hidráulicos de alta presión",
    de: "Hochdruck-Hydraulikkreise",
    fr: "Circuits hydrauliques haute pression",
    pt: "Circuitos hidráulicos de alta pressão"
  },
  meshType: {
    en: "Dutch Twilled Weave",
    zh: "斜纹密纹网",
    es: "Tejido Dutch cruzado",
    de: "Köpertresse",
    fr: "Tissage hollandais sergé",
    pt: "Trama holandesa sarja"
  },
  micron: "3 – 15 µm",
  alloy: "316L Sintered"
}, {
  id: "oil-2",
  application: {
    en: "Heavy Fuel Oil & Bunker Straining",
    zh: "重油与船用燃油粗滤",
    es: "Filtración gruesa de fuelóleo pesado y bunkering",
    de: "Schweröl- & Schiffskraftstoff-Grobsiebung",
    fr: "Filtration grossière de fioul lourd et soutage",
    pt: "Filtragem grossa de óleo combustível pesado e bunker"
  },
  meshType: {
    en: "Reinforced Square Mesh",
    zh: "加筋方孔网",
    es: "Malla cuadrada reforzada",
    de: "Verstärktes Quadratgewebe",
    fr: "Toile carrée renforcée",
    pt: "Malha quadrada reforçada"
  },
  micron: "100 – 400 µm",
  alloy: "304 / 316"
}, {
  id: "oil-3",
  application: {
    en: "Aviation & Diesel Fuel Dehydration",
    zh: "航空与柴油脱水聚结",
    es: "Deshidratación de combustible de aviación y diésel",
    de: "Kerosin- & Diesel-Entwässerung",
    fr: "Déshydratation de carburant aviation et diesel",
    pt: "Desidratação de combustível de aviação e diesel"
  },
  meshType: {
    en: "Plain Dutch (Coalescing)",
    zh: "平纹密纹网 (聚结)",
    es: "Dutch liso (coalescencia)",
    de: "Glatte Tresse (Koaleszenz)",
    fr: "Hollandais uni (coalescence)",
    pt: "Holandesa lisa (coalescência)"
  },
  micron: "10 – 40 µm",
  alloy: "316L Hydrophobic"
}, {
  id: "oil-4",
  application: {
    en: "Turbine Lube Oil Recirculation",
    zh: "透平润滑油循环系统",
    es: "Recirculación de aceite lubricante de turbinas",
    de: "Turbinen-Schmierölkreislauf",
    fr: "Recirculation d'huile de turbine",
    pt: "Recirculação de óleo lubrificante de turbinas"
  },
  meshType: {
    en: "5-Layer Sintered Wire Mesh",
    zh: "五层烧结金属丝网",
    es: "Malla sinterizada de 5 capas",
    de: "5-lagiges Sinterdrahtgewebe",
    fr: "Toile métallique frittée 5 couches",
    pt: "Malha de arame sinterizada de 5 camadas"
  },
  micron: "5 – 25 µm",
  alloy: "316L"
}];
const chemicalSpecs: SpecRow[] = [{
  id: "chem-1",
  application: {
    en: "Corrosive Acid & Alkali Filtration",
    zh: "强酸与强碱腐蚀性介质过滤",
    es: "Filtración de ácidos y álcalis corrosivos",
    de: "Filtration korrosiver Säuren & Laugen",
    fr: "Filtration d'acides et bases corrosifs",
    pt: "Filtração de ácidos e álcalis corrosivos"
  },
  meshType: {
    en: "Plain Dutch / Twill Dutch",
    zh: "平纹 / 斜纹密纹网",
    es: "Dutch liso / Dutch cruzado",
    de: "Glatte Tresse / Köpertresse",
    fr: "Hollandais uni / Hollandais sergé",
    pt: "Holandesa lisa / Holandesa sarja"
  },
  micron: "5 – 50 µm",
  alloy: "Hastelloy / 316L"
}, {
  id: "chem-2",
  application: {
    en: "Solvent & Catalyst Recovery",
    zh: "溶剂精馏与催化剂回收",
    es: "Recuperación de solventes y catalizadores",
    de: "Lösungsmittel- & Katalysator-Rückgewinnung",
    fr: "Récupération de solvants et catalyseurs",
    pt: "Recuperação de solventes e catalisadores"
  },
  meshType: {
    en: "Sintered Mesh Laminates",
    zh: "烧结金属层叠板",
    es: "Laminados sinterizados",
    de: "Sintergewebelaminate",
    fr: "Stratifiés frittés",
    pt: "Laminados sinterizados"
  },
  micron: "1 – 20 µm",
  alloy: "316L / Hastelloy C-276"
}, {
  id: "chem-3",
  application: {
    en: "Polymer Melt & Hot Resin Straining",
    zh: "高温聚合物熔体与树脂过滤",
    es: "Polímeros fundidos y resinas calientes",
    de: "Polymerschmelze- & Heißharzfiltration",
    fr: "Polymères fondus et résines chaudes",
    pt: "Polímeros fundidos e resinas aquecidas"
  },
  meshType: {
    en: "High-Tensile Twilled Weave",
    zh: "高抗拉斜纹编织网",
    es: "Tejido sarga de alta resistencia",
    de: "Hochfestes Köpergewebe",
    fr: "Tissage sergé haute résistance",
    pt: "Trama sarja de alta resistência"
  },
  micron: "15 – 80 µm",
  alloy: "316L"
}, {
  id: "chem-4",
  application: {
    en: "Specialty Chemical Reaction Vessels",
    zh: "特种化学反应釜过滤",
    es: "Reactores químicos especiales",
    de: "Spezialchemische Reaktionsbehälter",
    fr: "Réacteurs chimiques spéciaux",
    pt: "Vasos de reação química especiais"
  },
  meshType: {
    en: "Reverse Dutch Weave (PZ)",
    zh: "反向密纹网 (PZ)",
    es: "Tejido Dutch invertido (PZ)",
    de: "Breite Tresse / Panzergewebe (PZ)",
    fr: "Tissage hollandais inversé (PZ)",
    pt: "Trama holandesa invertida (PZ)"
  },
  micron: "10 – 60 µm",
  alloy: "Titanium / 316L"
}];
const guideCopy = {
  applicationNote: {
    en: "Technical Application Note",
    zh: "技术应用说明",
    es: "Nota técnica de aplicación",
    de: "Technische Anwendungsnotiz",
    fr: "Note technique d'application",
    pt: "Nota técnica de aplicação"
  },
  h2: {
    en: "Liquid filtration mesh selection guide",
    zh: "液体过滤丝网选型指南",
    es: "Guía de selección de malla para líquidos",
    de: "Auswahlleitfaden für Flüssigkeitsfiltergewebe",
    fr: "Guide de sélection de toile pour liquides",
    pt: "Guia de seleção de malha para líquidos"
  },
  lead: {
    en: "Match fluid viscosity, operating temperature, and micron targets to the right stainless steel weave and alloy. Built for engineers sizing industrial strainers, filter cartridges, and backwash elements.",
    zh: "结合流体粘度、操作温度与微米级过滤精度，匹配合适的不锈钢编织形式与合金材质。专为工业过滤器、滤芯及反冲洗滤网的工程选型设计。",
    es: "Empareje viscosidad, temperatura de servicio y micraje con el tejido de acero inoxidable y la aleación adecuados. Desarrollado para el dimensionamiento de filtros industriales y elementos de retrolavado.",
    de: "Stimmen Sie Viskosität, Betriebstemperatur und Mikronwerte auf das passende Edelstahlgewebe und die Legierung ab. Entwickelt für die Auslegung industrieller Siebe, Filterkerzen und Rückspülelemente.",
    fr: "Associez viscosité, température et micronage au bon tissage et alliage inox. Conçu pour le dimensionnement de filtres industriels, cartouches et éléments rétrolavables.",
    pt: "Combine viscosidade, temperatura operacional e micragem com a malha de aço inoxidável e liga corretas. Desenvolvido para engenheiros que dimensionam cestos, cartuchos e elementos de retrolavagem."
  },
  domainsEyebrow: {
    en: "Application Domains",
    zh: "核心应用领域",
    es: "Áreas de aplicación",
    de: "Anwendungsbereiche",
    fr: "Domaines d'application",
    pt: "Domínios de aplicação"
  },
  domainsHeading: {
    en: "Engineered for distinct liquid environments",
    zh: "针对不同液体工况与介质专门工程设计",
    es: "Diseño para diferentes entornos y fluidos líquidos",
    de: "Entwickelt für unterschiedliche flüssige Medien",
    fr: "Conçue pour différents environnements liquides",
    pt: "Desenvolvida para diferentes ambientes de líquidos"
  },
  domainsDesc: {
    en: "Select an operational medium to view specialized aperture ranges, flow dynamics, and weave recommendations.",
    zh: "选择具体流体介质，查看针对性的过滤孔径范围、流体动力学表现及推荐编织结构。",
    es: "Seleccione un fluido para consultar rangos de abertura, dinámica de flujo y tejidos recomendados.",
    de: "Wählen Sie ein Betriebsmedium zur Anzeige passender Maschenweiten, Strömungsprofile und Gewebeempfehlungen.",
    fr: "Sélectionnez un fluide pour consulter les ouvertures adaptées, la dynamique d'écoulement et les tissages préconisés.",
    pt: "Selecione um fluido operacional para verificar faixas de abertura, dinâmica de fluxo e tecidos recomendados."
  },
  specLink: {
    en: "Specifications",
    zh: "详细规格",
    es: "Especificaciones",
    de: "Spezifikationen",
    fr: "Spécifications",
    pt: "Especificações"
  },
  workflowHeading: {
    en: "4-stage engineering specification protocol",
    zh: "四步工程选型与规格确定流程",
    es: "Protocolo de especificación técnica en 4 etapas",
    de: "4-Stufen-Ingenieurprotokoll zur Spezifikation",
    fr: "Protocole d'ingénierie en 4 étapes",
    pt: "Protocolo de especificação técnica em 4 etapas"
  },
  workflowSub: {
    en: "Standard methodology for OEM & process sizing",
    zh: "OEM 及工艺过滤系统设计的标准工程方法",
    es: "Metodología estándar para diseño OEM y de proceso",
    de: "Standardmethode für OEM- und Prozessauslegung",
    fr: "Méthode standard pour le dimensionnement OEM et procédé",
    pt: "Metodologia padrão para dimensionamento OEM e de processo"
  },
  primaryParam: {
    en: "Primary parameter:",
    zh: "主要考量参数：",
    es: "Parámetro principal:",
    de: "Hauptparameter:",
    fr: "Paramètre principal :",
    pt: "Parâmetro principal:"
  },
  matricesHeading: {
    en: "Media-specific selection matrices",
    zh: "不同流体介质选型对照矩阵",
    es: "Matrices de selección según fluido",
    de: "Medienspezifische Auswahlmatrizen",
    fr: "Matrices de sélection par fluide",
    pt: "Matrizes de seleção por fluido"
  },
  matricesDesc: {
    en: "Compare weave mechanics, aperture stability, and clean pressure drops across primary liquid categories.",
    zh: "对比主要液体类别的编织机理、孔径结构稳定性及初始洁净压降特性。",
    es: "Compare mecánica del tejido, estabilidad de poro y caídas de presión limpias entre categorías de líquidos.",
    de: "Vergleichen Sie Webmechanik, Porenstabilität und Reindruckverluste über die wichtigsten Flüssigkeitsklassen.",
    fr: "Comparez la tenue mécanique, la stabilité des pores et les pertes de charge propres par catégorie de liquide.",
    pt: "Compare a mecânica de tecelagem, estabilidade de poro e perdas de carga limpas entre categorias de fluidos."
  },
  tabWater: {
    en: "Water & Aqueous",
    zh: "水及水相介质",
    es: "Agua y medios acuosos",
    de: "Wasser & wässrige Medien",
    fr: "Eau et milieux aqueux",
    pt: "Água e meios aquosos"
  },
  tabOil: {
    en: "Oil & Fuels",
    zh: "油品与燃油",
    es: "Aceite y combustibles",
    de: "Öl & Kraftstoffe",
    fr: "Huiles et carburants",
    pt: "Óleos e combustíveis"
  },
  tabChemical: {
    en: "Chemical & Solvents",
    zh: "化工与溶剂",
    es: "Química y solventes",
    de: "Chemie & Lösungsmittel",
    fr: "Chimie et solvants",
    pt: "Química e solventes"
  },
  fullChartsBtn: {
    en: "View full aperture charts",
    zh: "查看完整孔径规格表",
    es: "Ver tablas completas de abertura",
    de: "Vollständige Maschentabellen anzeigen",
    fr: "Voir les tableaux d'ouverture complets",
    pt: "Ver tabelas completas de abertura"
  },
  waterCardTitle: {
    en: "Water & cooling systems",
    zh: "水处理与冷却循环系统",
    es: "Sistemas de agua y refrigeración",
    de: "Wasser- und Kühlsysteme",
    fr: "Systèmes d'eau et de refroidissement",
    pt: "Sistemas de água e resfriamento"
  },
  waterCardDesc: {
    en: "High throughput, continuous backwash cycles, and particulate sediment removal.",
    zh: "高流通量、连续反冲洗循环工况以及颗粒沉淀物截留滤除。",
    es: "Alto caudal, ciclos continuos de retrolavado y retención de sedimentos particulados.",
    de: "Hoher Durchsatz, kontinuierliche Rückspülzyklen und Sedimentabscheidung.",
    fr: "Débits élevés, cycles de rétrolavage continus et élimination des sédiments.",
    pt: "Alta vazão, ciclos contínuos de retrolavagem e retenção de sedimentos particulados."
  },
  viscosityWater: {
    en: "Viscosity: 1 – 10 cSt",
    zh: "粘度范围: 1 – 10 cSt",
    es: "Viscosidad: 1 – 10 cSt",
    de: "Viskosität: 1 – 10 cSt",
    fr: "Viscosité : 1 – 10 cSt",
    pt: "Viscosidade: 1 – 10 cSt"
  },
  viscosityOil: {
    en: "Viscosity: 10 – 500+ cSt",
    zh: "粘度范围: 10 – 500+ cSt",
    es: "Viscosidad: 10 – 500+ cSt",
    de: "Viskosität: 10 – 500+ cSt",
    fr: "Viscosité : 10 – 500+ cSt",
    pt: "Viscosidade: 10 – 500+ cSt"
  },
  viscosityChem: {
    en: "pH: 0.5 – 14 | Severe Media",
    zh: "pH: 0.5 – 14 | 苛刻介质",
    es: "pH: 0,5 – 14 | Medios severos",
    de: "pH: 0,5 – 14 | Aggressive Medien",
    fr: "pH : 0,5 – 14 | Milieux sévères",
    pt: "pH: 0,5 – 14 | Meios severos"
  },
  typicalAlloys: {
    en: "Typical Alloys:",
    zh: "常用合金材质：",
    es: "Aleaciones típicas:",
    de: "Typische Legierungen:",
    fr: "Alliages typiques :",
    pt: "Ligas típicas:"
  },
  weaveArchitecture: {
    en: "Weave Architecture:",
    zh: "编织结构形式：",
    es: "Estructura de tejido:",
    de: "Gewebeaufbau:",
    fr: "Architecture de tissage :",
    pt: "Estrutura de trama:"
  },
  waterTableTooltip: {
    en: "Standard mesh configurations for typical process water loops",
    zh: "典型工艺水循环的标准丝网配置",
    es: "Configuraciones estándar de malla para circuitos típicos de agua",
    de: "Standardgewebekonfigurationen für typische Prozesswasserkreisläufe",
    fr: "Configurations de toile standard pour boucles d'eau de procédé typiques",
    pt: "Configurações padrão de malha para circuitos típicos de água de processo"
  },
  oilTableTooltip: {
    en: "Standard mesh configurations for typical lubrication and fuel circuits",
    zh: "典型润滑油与燃油管路系统的标准丝网配置",
    es: "Configuraciones estándar de malla para circuitos típicos de lubricación y combustible",
    de: "Standardgewebekonfigurationen für typische Schmier- und Kraftstoffkreisläufe",
    fr: "Configurations de toile standard pour circuits typiques de lubrification et carburant",
    pt: "Configurações padrão de malha para circuitos típicos de lubrificação e combustível"
  },
  chemTableTooltip: {
    en: "Material verification reports available upon specification in the purchase order.",
    zh: "对于适用的产品或订单，可根据采购订单具体约定提供材质验证报告。",
    es: "Informes de verificación de materiales disponibles según especificación en el pedido.",
    de: "Materialprüfberichte bei entsprechender Vereinbarung im Auftrag verfügbar.",
    fr: "Rapports de vérification matière disponibles si spécifiés au bon de commande.",
    pt: "Relatórios de verificação de material disponíveis conforme especificação no pedido de compra."
  },
  thDuty: {
    en: "Duty / Application",
    zh: "工况 / 应用领域",
    es: "Servicio / Aplicación",
    de: "Betrieb / Anwendung",
    fr: "Service / Application",
    pt: "Serviço / Aplicação"
  },
  thMesh: {
    en: "Mesh Configuration",
    zh: "丝网编织形式",
    es: "Configuración de malla",
    de: "Gewebekonfiguration",
    fr: "Configuration de toile",
    pt: "Configuração de malha"
  },
  thRetention: {
    en: "Retention",
    zh: "截留精度",
    es: "Retención",
    de: "Abscheidung",
    fr: "Rétention",
    pt: "Retenção"
  },
  thGrade: {
    en: "Standard Grade",
    zh: "推荐材质",
    es: "Calidad estándar",
    de: "Standardgüte",
    fr: "Nuance standard",
    pt: "Grau padrão"
  },
  discsPrompt: {
    en: "Need non-standard mesh counts or custom punched circular discs?",
    zh: "需要非标目数或定制冲压圆形滤片？",
    es: "¿Necesita números de malla especiales o discos estampados a medida?",
    de: "Benötigen Sie Sonder-Maschenzahlen oder gestanzte Ronden nach Maß?",
    fr: "Besoin de maillages hors standard ou de disques découpés sur mesure ?",
    pt: "Precisa de contagens de malha especiais ou discos estampados sob medida?"
  },
  discsLink: {
    en: "Stamped discs & coils",
    zh: "冲压滤片与分条网带",
    es: "Discos estampados y bobinas",
    de: "Stanzronden & Bänder",
    fr: "Disques découpés et bandes",
    pt: "Discos estampados e bobinas"
  },
  sinteredPrompt: {
    en: "Require sintered multi-layer laminate construction for high collapse resistance?",
    zh: "需要高耐破压的多层烧结复合网结构？",
    es: "¿Requiere laminados multicapa sinterizados para alta resistencia al colapso?",
    de: "Benötigen Sie mehrlagige Sinterlaminate für hohe Kollapsfestigkeit?",
    fr: "Besoin de stratifiés multicouches frittés pour une haute résistance à l'écrasement ?",
    pt: "Necessita de laminados multicamadas sinterizados para alta resistência ao colapso?"
  },
  sinteredLink: {
    en: "Sintered laminate options",
    zh: "烧结复合网解决方案",
    es: "Opciones de laminados sinterizados",
    de: "Sinterlaminat-Optionen",
    fr: "Options de toiles frittées",
    pt: "Opções de laminados sinterizados"
  },
  corrosionPrompt: {
    en: "Check comprehensive chemical compatibility matrix for specialty alloys?",
    zh: "查阅特种耐腐蚀合金完整化学兼容性对照表？",
    es: "¿Desea consultar la matriz completa de compatibilidad química para aleaciones?",
    de: "Vollständige chemische Beständigkeitsmatrix für Sonderlegierungen prüfen?",
    fr: "Consulter la matrice complète de compatibilité chimique pour alliages spéciaux ?",
    pt: "Consultar a matriz completa de compatibilidade química para ligas especiais?"
  },
  corrosionLink: {
    en: "Corrosion resistance charts",
    zh: "耐腐蚀对照图表",
    es: "Tablas de resistencia a la corrosión",
    de: "Korrosionsbeständigkeitstabellen",
    fr: "Tableaux de résistance à la corrosion",
    pt: "Tabelas de resistência à corrosão"
  },
  calcStripHeading: {
    en: "Differential pressure (ΔP) and open area calculations",
    zh: "压降（ΔP）与有效开孔面积工程计算",
    es: "Cálculos de pérdida de carga (ΔP) y área abierta",
    de: "Berechnungen zu Differenzdruck (ΔP) und freier Siebfläche",
    fr: "Calculs de perte de charge (ΔP) et de surface ouverte",
    pt: "Cálculos de queda de pressão (ΔP) e área aberta"
  },
  calcStripDesc: {
    en: "Liquid flow velocity through woven wire cloth follows Darcy-Forchheimer permeability models. For accurate screen sizing, submit your flow rate, viscosity, operating temperature, and allowable dirty pressure drop.",
    zh: "液体穿过金属编织网的流动遵循 Darcy-Forchheimer 渗透模型。如需精确选型，请提交流量、介质粘度、工作温度及允许终阻压降要求。",
    es: "El flujo de líquidos a través de mallas tejidas sigue modelos de permeabilidad Darcy-Forchheimer. Para un dimensionamiento preciso, envíe caudal, viscosidad, temperatura y pérdida admisible.",
    de: "Der Durchfluss von Flüssigkeiten durch Drahtgewebe folgt Darcy-Forchheimer-Permeabilitätsmodellen. Für die präzise Auslegung übermitteln Sie Durchfluss, Viskosität, Temperatur und zulässigen Schmutzdruck.",
    fr: "L'écoulement liquide à travers une toile métallique suit les modèles de Darcy-Forchheimer. Pour un dimensionnement précis, transmettez débit, viscosité, température et perte de charge admissible.",
    pt: "O fluxo de líquidos através de telas tecidas segue os modelos de Darcy-Forchheimer. Para dimensionamento preciso, envie vazão, viscosidade, temperatura e perda de carga admissível."
  },
  calcStripBtn1: {
    en: "Request custom sizing",
    zh: "获取定制选型支持",
    es: "Solicitar dimensionamiento a medida",
    de: "Individuelle Auslegung anfragen",
    fr: "Demander un dimensionnement sur mesure",
    pt: "Solicitar dimensionamento sob medida"
  },
  calcStripBtn2: {
    en: "Delta P models",
    zh: "压降计算模型",
    es: "Modelos de Delta P",
    de: "Delta-P-Modelle",
    fr: "Modèles Delta P",
    pt: "Modelos de Delta P"
  },
  strip1Title: {
    en: "Weave Architectures",
    zh: "编织结构体系",
    es: "Arquitecturas de tejido",
    de: "Gewebearchitekturen",
    fr: "Architectures de tissage",
    pt: "Arquiteturas de trama"
  },
  strip1Desc: {
    en: "Square weave, plain Dutch, and reverse twill comparison.",
    zh: "方孔网、平纹密纹网与斜纹反向密纹网对比。",
    es: "Comparativa de tejido cuadrado, Dutch liso y sarga invertida.",
    de: "Vergleich von Quadratmasche, glatter Tresse und Panzergewebe.",
    fr: "Comparaison toile carrée, hollandais uni et sergé inversé.",
    pt: "Comparação de malha quadrada, holandesa lisa e sarja invertida."
  },
  strip1Link: {
    en: "Browse woven mesh styles",
    zh: "浏览编织网规格",
    es: "Ver tipos de malla tejida",
    de: "Drahtgewebe-Typen ansehen",
    fr: "Découvrir les toiles tissées",
    pt: "Ver tipos de malhas tecidas"
  },
  strip2Title: {
    en: "Material Verification",
    zh: "材料质量验证",
    es: "Verificación de materiales",
    de: "Werkstoffprüfung",
    fr: "Contrôle des matériaux",
    pt: "Verificação de materiais"
  },
  strip2Desc: {
    en: "PMI spectroscopy and raw alloy mill test reports.",
    zh: "PMI 光谱材质分析与钢厂原厂质量证明书。",
    es: "Espectrometría PMI e informes de prueba de fábrica.",
    de: "PMI-Spektroskopie und Werksprüfzeugnisse der Rohlegierungen.",
    fr: "Spectrométrie PMI et certificats de matière d'usine.",
    pt: "Espectrometria PMI e certificados de teste de usina."
  },
  strip2Link: {
    en: "Quality verification data",
    zh: "质量检测与认证数据",
    es: "Datos de verificación de calidad",
    de: "Qualitätsprüfungsdaten",
    fr: "Données de contrôle qualité",
    pt: "Dados de controle de qualidade"
  },
  strip3Title: {
    en: "Micron Rating Guide",
    zh: "微米精度指南",
    es: "Guía de clasificación en micras",
    de: "Leitfaden für Mikronwerte",
    fr: "Guide des seuils en microns",
    pt: "Guia de classificação em mícrons"
  },
  strip3Desc: {
    en: "Bubble point testing and ASTM E2016 aperture tolerances.",
    zh: "泡点试验方法与 ASTM E2016 孔径公差标准。",
    es: "Ensayo de punto de burbuja y tolerancias según ASTM E2016.",
    de: "Blaspunktprüfung und Maschentoleranzen nach ASTM E2016.",
    fr: "Essai au point de bulle et tolérances selon ASTM E2016.",
    pt: "Ensaio de ponto de bolha e tolerâncias ASTM E2016."
  },
  strip3Link: {
    en: "Aperture to micron charts",
    zh: "孔径与微米对照表",
    es: "Tablas de abertura a micras",
    de: "Tabellen Maschenweite zu Mikron",
    fr: "Tableaux d'ouverture en microns",
    pt: "Tabelas de abertura para mícrons"
  },
  strip4Title: {
    en: "Filter Cartridges & Tubes",
    zh: "滤芯与过滤管件",
    es: "Cartuchos y tubos filtrantes",
    de: "Filterkerzen & Filterrohre",
    fr: "Cartouches et tubes filtrants",
    pt: "Cartuchos e tubos filtrantes"
  },
  strip4Desc: {
    en: "Pleated cylinders, welded filter tubes, and strainer baskets.",
    zh: "折波滤芯、焊接过滤管与篮式过滤网篮。",
    es: "Cilindros plisados, tubos soldados y cestas filtrantes.",
    de: "Plissierte Zylinder, geschweißte Filterrohre und Siebkörbe.",
    fr: "Cylindres plissés, tubes soudés et paniers filtrants.",
    pt: "Cilindros pregueados, tubos soldados e cestos filtrantes."
  },
  strip4Link: {
    en: "View cylindrical filters",
    zh: "查看筒状过滤元件",
    es: "Ver filtros cilíndricos",
    de: "Zylinderfilter ansehen",
    fr: "Voir les filtres cylindriques",
    pt: "Ver filtros cilíndricos"
  },
  exploreWaterBtn: {
    en: "Explore water filtration mesh",
    zh: "探索水过滤丝网产品",
    es: "Explorar mallas para agua",
    de: "Wasserfiltergewebe entdecken",
    fr: "Découvrir les toiles pour eau",
    pt: "Explorar malhas para água"
  },
  exploreOilBtn: {
    en: "Explore fuel & hydraulic mesh",
    zh: "探索燃油与液压过滤丝网",
    es: "Explorar mallas para combustible e hidráulica",
    de: "Gewebe für Kraftstoff & Hydraulik",
    fr: "Découvrir les toiles carburant et hydraulique",
    pt: "Explorar malhas para combustível e hidráulica"
  },
  exploreChemBtn: {
    en: "Explore chemical filtration mesh",
    zh: "探索化工过滤丝网",
    es: "Explorar mallas para química",
    de: "Chemiefiltergewebe entdecken",
    fr: "Découvrir les toiles chimiques",
    pt: "Explorar malhas químicas"
  }
};
export default function ProcessGuide() {
  const {
    lang
  } = useLanguage();
  const [activeMedium, setActiveMedium] = useState<string>("water");
  const columns: DataTableColumn<SpecRow>[] = useMemo(() => [{
    key: "application",
    header: pick(lang, guideCopy.thDuty),
    cell: row => <span className="font-medium text-foreground">{pick(lang, row.application)}</span>,
    sticky: true
  }, {
    key: "meshType",
    header: pick(lang, guideCopy.thMesh),
    cell: row => <span className="text-muted-foreground">{pick(lang, row.meshType)}</span>
  }, {
    key: "micron",
    header: pick(lang, guideCopy.thRetention),
    cell: row => <Badge variant="outline" className="font-mono mf-caption py-0.5 border-border">
            {row.micron}
          </Badge>,
    emphasis: true
  }, {
    key: "alloy",
    header: pick(lang, guideCopy.thGrade),
    cell: row => <span className="font-mono text-muted-foreground">{row.alloy}</span>
  }], [lang]);
  return <section id="liquidfiltrationprocessguide" data-nav="light" className="relative w-full bg-muted/50 text-foreground mf-section border-b border-border">
      <div className="mf-container">
        {/* Header Block */}
        <Reveal>
          <div className="max-w-3xl mb-12 md:mb-16">
            <div className="flex items-center gap-2 mb-3">
              <Badge variant="outline" className="mf-caption uppercase tracking-wider font-mono border-border bg-card">
                {pick(lang, guideCopy.applicationNote)}
              </Badge>
              <span className="mf-caption text-muted-foreground font-mono">ISO 9044 / ASTM E2016</span>
            </div>
            <h2 className="mf-h2 font-default font-semibold text-foreground">
              {pick(lang, guideCopy.h2)}
            </h2>
            <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-[65ch]">
              {pick(lang, guideCopy.lead)}
            </p>
          </div>
        </Reveal>

        {/* 4 Application Category Pillars (Water, Oil, Fuel, Chemical) */}
        <div className="mb-16">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 gap-4">
              <div>
                <p className="mf-caption font-mono uppercase tracking-wider text-primary font-semibold">
                  {pick(lang, guideCopy.domainsEyebrow)}
                </p>
                <h3 className="mf-h3 text-foreground font-default mt-1 font-semibold">
                  {pick(lang, guideCopy.domainsHeading)}
                </h3>
              </div>
              <p className="text-sm text-muted-foreground max-w-md">
                {pick(lang, guideCopy.domainsDesc)}
              </p>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {applicationPillars.map((app, i) => {
            const IconComp = app.icon;
            return <Reveal key={app.id} delay={i * 60}>
                  <Card data-index={i} className="group relative flex flex-col justify-between border border-border bg-card mf-hover-lift h-full">
                    <CardContent className="p-6 space-y-4 flex flex-col justify-between h-full">
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <div className="inline-flex size-10 items-center justify-center rounded-md border border-border bg-muted text-primary">
                            <IconComp className="size-5" aria-hidden="true" />
                          </div>
                          <span className="mf-caption font-mono font-semibold text-muted-foreground bg-muted px-2 py-0.5 rounded">
                            {pick(lang, app.metric)}
                          </span>
                        </div>

                        <div className="space-y-1.5">
                          <h4 className="text-base font-semibold text-foreground font-default">
                            {pick(lang, app.title)}
                          </h4>
                          <p className="text-sm text-muted-foreground leading-relaxed">
                            {pick(lang, app.description)}
                          </p>
                        </div>
                      </div>

                      <div className="pt-3 border-t border-border flex items-center justify-between mf-caption mt-auto">
                        <span className="text-muted-foreground font-mono">{app.grade}</span>
                        <Link to={app.to} className="font-medium text-foreground hover:text-primary inline-flex items-center gap-1 group-hover:underline">
                          <span>{pick(lang, guideCopy.specLink)}</span>
                          <ArrowRight className="size-3.5" aria-hidden="true" />
                        </Link>
                      </div>
                    </CardContent>
                  </Card>
                </Reveal>;
          })}
          </div>
        </div>

        {/* 4-Step Engineering Process Workflow */}
        <div className="mb-16">
          <Reveal>
            <div className="flex items-center justify-between mb-6 pb-2 border-b border-border">
              <div className="flex items-center gap-2">
                <Sliders className="size-5 text-primary" aria-hidden="true" />
                <h3 className="mf-h3 font-default font-semibold text-foreground">
                  {pick(lang, guideCopy.workflowHeading)}
                </h3>
              </div>
              <span className="mf-caption text-muted-foreground hidden sm:inline-block">
                {pick(lang, guideCopy.workflowSub)}
              </span>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {processSteps.map((step, i) => <Reveal key={step.step} delay={i * 60}>
                <div data-index={i} className="bg-card border border-border rounded-lg p-5 flex flex-col justify-between h-full">
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-2xl font-mono font-bold text-primary">
                        {step.step}
                      </span>
                      <span className="size-2 rounded-full bg-primary/30" />
                    </div>
                    <h4 className="text-base font-default font-semibold text-foreground mb-2">
                      {pick(lang, step.title)}
                    </h4>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {pick(lang, step.desc)}
                    </p>
                  </div>
                  <div className="mt-4 pt-3 border-t border-border">
                    <span className="mf-caption font-mono text-muted-foreground block uppercase">
                      {pick(lang, guideCopy.primaryParam)}
                    </span>
                    <span className="text-sm font-medium text-foreground mt-0.5 block">
                      {pick(lang, step.focus)}
                    </span>
                  </div>
                </div>
              </Reveal>)}
          </div>
        </div>

        {/* Medium Specific Selection Guides with Tabs */}
        <div className="mb-16">
          <Reveal>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="mf-h3 font-default font-semibold text-foreground">
                  {pick(lang, guideCopy.matricesHeading)}
                </h3>
                <p className="text-sm text-muted-foreground mt-1 max-w-[65ch]">
                  {pick(lang, guideCopy.matricesDesc)}
                </p>
              </div>
            </div>
          </Reveal>

          <Tabs value={activeMedium} onValueChange={setActiveMedium} className="w-full">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
              <TabsList className="grid grid-cols-3 w-full sm:w-auto h-11 bg-muted p-1 rounded-lg">
                <TabsTrigger value="water" className="group flex items-center gap-2 px-4 py-2 text-sm font-medium data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                  <Droplets className="size-4 text-chart-3 group-data-[state=active]:text-primary-foreground" />
                  <span>{pick(lang, guideCopy.tabWater)}</span>
                </TabsTrigger>
                <TabsTrigger value="oil" className="group flex items-center gap-2 px-4 py-2 text-sm font-medium data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                  <Fuel className="size-4 text-chart-5 group-data-[state=active]:text-primary-foreground" />
                  <span>{pick(lang, guideCopy.tabOil)}</span>
                </TabsTrigger>
                <TabsTrigger value="chemical" className="group flex items-center gap-2 px-4 py-2 text-sm font-medium data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                  <FlaskConical className="size-4 text-chart-4 group-data-[state=active]:text-primary-foreground" />
                  <span>{pick(lang, guideCopy.tabChemical)}</span>
                </TabsTrigger>
              </TabsList>

              <div className="flex items-center gap-2">
                <Button asChild variant="outline" size="sm" className="text-sm">
                  <Link to="/TechnicalChartsPage">
                    <FileText className="size-4 mr-1.5" aria-hidden="true" />
                    {pick(lang, guideCopy.fullChartsBtn)}
                  </Link>
                </Button>
              </div>
            </div>

            {/* Water Tab Content */}
            <TabsContent value="water" className="space-y-6 focus-visible:outline-none">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
                {/* Left Specs Summary Card */}
                <Card className="border border-border bg-card">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <Badge variant="outline" className="font-mono mf-caption bg-muted text-foreground border border-border">
                        {pick(lang, guideCopy.viscosityWater)}
                      </Badge>
                      <Droplets className="size-5 text-chart-3" />
                    </div>
                    <CardTitle className="mf-h3 mt-2 font-default">
                      {pick(lang, guideCopy.waterCardTitle)}
                    </CardTitle>
                    <CardDescription className="text-sm">
                      {pick(lang, guideCopy.waterCardDesc)}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4 text-sm">
                    <div className="p-3 bg-muted/60 rounded-md space-y-2">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{pick(lang, guideCopy.typicalAlloys)}</span>
                        <span className="font-mono font-medium text-foreground">AISI 304, 316L</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{pick(lang, guideCopy.weaveArchitecture)}</span>
                        <span className="font-medium text-foreground">
                          {pick(lang, {
                          en: "Square Weave / Sintered Multi-layer",
                          zh: "平纹方孔网 / 多层烧结网",
                          es: "Malla cuadrada / Multicapa sinterizada",
                          de: "Quadratgewebe / Mehrlagiges Sintergewebe",
                          fr: "Toile carrée / Multicouche fritté",
                          pt: "Malha quadrada / Multicamadas sinterizada"
                        })}
                        </span>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <h4 className="font-medium text-foreground text-sm">{pick(lang, guideCopy.criticalFactors)}</h4>
                      <ul className="space-y-1.5 text-muted-foreground text-sm">
                        {guideCopy.waterFactors.map((f, i) => <li key={i} className="flex items-start gap-2">
                            <CheckCircle2 className="size-4 text-chart-4 mt-0.5 shrink-0" />
                            <span>{pick(lang, f)}</span>
                          </li>)}
                      </ul>
                    </div>

                      <Button asChild variant="secondary" size="sm" className="w-full text-sm mt-2">
                      <Link to="/WaterLiquidFiltrationPage">
                        {pick(lang, guideCopy.exploreWaterBtn)}
                        <ArrowRight className="size-4 ml-1.5" aria-hidden="true" />
                      </Link>
                    </Button>
                  </CardContent>
                </Card>

                {/* Right Technical Table */}
                <div className="lg:col-span-2 space-y-4">
                  <DataTable columns={columns} rows={waterSpecs} getRowKey={r => r.id} caption={pick(lang, guideCopy.waterTableTooltip)} />

                  <div className="p-3 bg-card border border-border rounded-lg flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 text-sm text-muted-foreground">
                    <span>{pick(lang, guideCopy.discsPrompt)}</span>
                    <Link to="/SlitCoilsAndStampedFilterDiscsPage" className="font-medium text-primary hover:underline inline-flex items-center gap-1 shrink-0">
                      {pick(lang, guideCopy.discsLink)}{" "}
                      <ArrowRight className="size-3.5" />
                    </Link>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Oil Tab Content */}
            <TabsContent value="oil" className="space-y-6 focus-visible:outline-none">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
                {/* Left Specs Summary Card */}
                <Card className="border border-border bg-card">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <Badge variant="outline" className="font-mono mf-caption bg-muted text-foreground border border-border">
                        {pick(lang, guideCopy.viscosityOil)}
                      </Badge>
                      <Fuel className="size-5 text-chart-5" />
                    </div>
                    <CardTitle className="mf-h3 mt-2 font-default">
                      {pick(lang, guideCopy.oilCardTitle)}
                    </CardTitle>
                    <CardDescription className="text-sm">
                      {pick(lang, guideCopy.oilCardDesc)}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4 text-sm">
                    <div className="p-3 bg-muted/60 rounded-md space-y-2">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{pick(lang, guideCopy.typicalAlloys)}</span>
                        <span className="font-mono font-medium text-foreground">316L, 304</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{pick(lang, guideCopy.weaveArchitecture)}</span>
                        <span className="font-medium text-foreground">
                          {pick(lang, {
                          en: "Plain & Twill Dutch (PDW/TDW)",
                          zh: "平纹/斜纹密纹网 (PDW/TDW)",
                          es: "Dutch liso y cruzado (PDW/TDW)",
                          de: "Glatte & Köpertressen (PDW/TDW)",
                          fr: "Tissages hollandais unis et sergés (PDW/TDW)",
                          pt: "Tramas holandesas lisas e sarjas (PDW/TDW)"
                        })}
                        </span>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <h4 className="font-medium text-foreground text-sm">{pick(lang, guideCopy.criticalFactors)}</h4>
                      <ul className="space-y-1.5 text-muted-foreground text-sm">
                        {guideCopy.oilFactors.map((f, i) => <li key={i} className="flex items-start gap-2">
                            <CheckCircle2 className="size-4 text-chart-4 mt-0.5 shrink-0" />
                            <span>{pick(lang, f)}</span>
                          </li>)}
                      </ul>
                    </div>

                    <Button asChild variant="secondary" size="sm" className="w-full text-sm mt-2">
                      <Link to="/OilFuelFiltrationPage">
                        {pick(lang, guideCopy.exploreOilBtn)}
                        <ArrowRight className="size-4 ml-1.5" aria-hidden="true" />
                      </Link>
                    </Button>
                  </CardContent>
                </Card>

                {/* Right Technical Table */}
                <div className="lg:col-span-2 space-y-4">
                  <DataTable columns={columns} rows={oilSpecs} getRowKey={r => r.id} caption={pick(lang, guideCopy.oilTableTooltip)} />

                  <div className="p-3 bg-card border border-border rounded-lg flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 text-sm text-muted-foreground">
                    <span>{pick(lang, guideCopy.sinteredPrompt)}</span>
                    <Link to="/SinteredMultilayerMeshPage" className="font-medium text-primary hover:underline inline-flex items-center gap-1 shrink-0">
                      {pick(lang, guideCopy.sinteredLink)}{" "}
                      <ArrowRight className="size-3.5" />
                    </Link>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Chemical Tab Content */}
            <TabsContent value="chemical" className="space-y-6 focus-visible:outline-none">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
                {/* Left Specs Summary Card */}
                <Card className="border border-border bg-card">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <Badge variant="outline" className="font-mono mf-caption bg-muted text-foreground border border-border">
                        {pick(lang, guideCopy.viscosityChem)}
                      </Badge>
                      <FlaskConical className="size-5 text-chart-4" />
                    </div>
                    <CardTitle className="mf-h3 mt-2 font-default">
                      {pick(lang, guideCopy.chemicalCardTitle)}
                    </CardTitle>
                    <CardDescription className="text-sm">
                      {pick(lang, guideCopy.chemicalCardDesc)}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4 text-sm">
                    <div className="p-3 bg-muted/60 rounded-md space-y-2">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{pick(lang, guideCopy.typicalAlloys)}</span>
                        <span className="font-mono font-medium text-foreground">316L, 316, Hastelloy</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{pick(lang, guideCopy.weaveArchitecture)}</span>
                        <span className="font-medium text-foreground">
                          {pick(lang, {
                          en: "Reverse Dutch (RPD) / Micro Dutch",
                          zh: "反向密纹 (RPD) / 微米密纹网",
                          es: "Dutch invertido (RPD) / Micro Dutch",
                          de: "Panzergewebe (RPD) / Mikrotresse",
                          fr: "Hollandais inversé (RPD) / Micro hollandais",
                          pt: "Holandesa invertida (RPD) / Micro holandesa"
                        })}
                        </span>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <h4 className="font-medium text-foreground text-sm">{pick(lang, guideCopy.criticalFactors)}</h4>
                      <ul className="space-y-1.5 text-muted-foreground text-sm">
                        {guideCopy.chemicalFactors.map((f, i) => <li key={i} className="flex items-start gap-2">
                            <CheckCircle2 className="size-4 text-chart-4 mt-0.5 shrink-0" />
                            <span>{pick(lang, f)}</span>
                          </li>)}
                      </ul>
                    </div>

                    <Button asChild variant="secondary" size="sm" className="w-full text-sm mt-2">
                      <Link to="/ChemicalFiltrationPage">
                        {pick(lang, guideCopy.exploreChemBtn)}
                        <ArrowRight className="size-4 ml-1.5" aria-hidden="true" />
                      </Link>
                    </Button>
                  </CardContent>
                </Card>

                {/* Right Technical Table */}
                <div className="lg:col-span-2 space-y-4">
                  <DataTable columns={columns} rows={chemicalSpecs} getRowKey={r => r.id} caption={pick(lang, guideCopy.chemTableTooltip)} />

                  <div className="p-3 bg-card border border-border rounded-lg flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 text-sm text-muted-foreground">
                    <span>{pick(lang, guideCopy.corrosionPrompt)}</span>
                    <Link to="/CorrosionResistanceMatricesPage" className="font-medium text-primary hover:underline inline-flex items-center gap-1 shrink-0">
                      {pick(lang, guideCopy.corrosionLink)}{" "}
                      <ArrowRight className="size-3.5" />
                    </Link>
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* Sizing & Delta P Calculation Notice Strip */}
        <div className="bg-surface-dark text-surface-dark-foreground rounded-lg p-6 md:p-8 border border-surface-dark-border">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
            <div className="md:col-span-2 space-y-2">
              <div className="flex items-center gap-2">
                <Gauge className="size-5 text-accent" />
                <h4 className="mf-h3 font-default font-semibold text-surface-dark-foreground">
                  {pick(lang, guideCopy.calcStripHeading)}
                </h4>
              </div>
              <p className="text-sm text-surface-dark-muted leading-relaxed max-w-[65ch]">
                {pick(lang, guideCopy.calcStripDesc)}
              </p>
            </div>

            <div className="flex flex-col sm:flex-row md:flex-col lg:flex-row gap-3 md:justify-end">
              <Button asChild variant="default" size="default">
                <Link to="/RequestAQuotePage">
                  {pick(lang, guideCopy.calcStripBtn1)}
                  <ArrowRight className="size-4 ml-1" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="default" className="border-surface-dark-border text-surface-dark-foreground hover:bg-surface-dark-elevated">
                <Link to="/CalculationGuidesPage">
                  {pick(lang, guideCopy.calcStripBtn2)}
                </Link>
              </Button>
            </div>
          </div>
        </div>

        {/* Technical Resource Quick Links Footer Strip */}
        <div className="mt-12 pt-8 border-t border-border grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="flex items-start gap-3">
            <Layers className="size-5 text-primary shrink-0 mt-0.5" />
            <div>
              <h4 className="font-semibold text-foreground block mb-1 text-sm">
                {pick(lang, guideCopy.strip1Title)}
              </h4>
              <p className="text-muted-foreground mb-1 text-sm leading-relaxed">
                {pick(lang, guideCopy.strip1Desc)}
              </p>
              <Link to="/WovenWireMeshPage" className="text-primary font-medium hover:underline text-sm inline-flex items-center gap-1">
                <span>{pick(lang, guideCopy.strip1Link)}</span>
                <ArrowRight className="size-3.5" aria-hidden="true" />
              </Link>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <ShieldCheck className="size-5 text-primary shrink-0 mt-0.5" aria-hidden="true" />
            <div>
              <h4 className="font-semibold text-foreground block mb-1 text-sm">
                {pick(lang, guideCopy.strip2Title)}
              </h4>
              <p className="text-muted-foreground mb-1 text-sm leading-relaxed">
                {pick(lang, guideCopy.strip2Desc)}
              </p>
              <Link to="/StainlessSteelMaterialVerificationPage" className="text-primary font-medium hover:underline text-sm inline-flex items-center gap-1">
                <span>{pick(lang, guideCopy.strip2Link)}</span>
                <ArrowRight className="size-3.5" aria-hidden="true" />
              </Link>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <FileText className="size-5 text-primary shrink-0 mt-0.5" aria-hidden="true" />
            <div>
              <h4 className="font-semibold text-foreground block mb-1 text-sm">
                {pick(lang, guideCopy.strip3Title)}
              </h4>
              <p className="text-muted-foreground mb-1 text-sm leading-relaxed">
                {pick(lang, guideCopy.strip3Desc)}
              </p>
              <Link to="/ApertureMicronRatingChartsPage" className="text-primary font-medium hover:underline text-sm inline-flex items-center gap-1">
                <span>{pick(lang, guideCopy.strip3Link)}</span>
                <ArrowRight className="size-3.5" aria-hidden="true" />
              </Link>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <Sliders className="size-5 text-primary shrink-0 mt-0.5" aria-hidden="true" />
            <div>
              <h4 className="font-semibold text-foreground block mb-1 text-sm">
                {pick(lang, guideCopy.strip4Title)}
              </h4>
              <p className="text-muted-foreground mb-1 text-sm leading-relaxed">
                {pick(lang, guideCopy.strip4Desc)}
              </p>
              <Link to="/FilterCartridgesPage" className="text-primary font-medium hover:underline text-sm inline-flex items-center gap-1">
                <span>{pick(lang, guideCopy.strip4Link)}</span>
                <ArrowRight className="size-3.5" aria-hidden="true" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>;
}